export const ESIGN_DOCUMENT_PURPOSES = [
  "NDA",
  "NDA_SELLER_BROKER",
  "NDA_BUYER_BROKER",
  "ACQUISITION_NDA",
  "LISTING_NDA",
  "LISTING_AGREEMENT",
  "LOI",
  "STOCK_PURCHASE_AGREEMENT",
  "ASSET_PURCHASE_AGREEMENT",
  "OTHER",
] as const;

export type EsignDocumentPurpose =
  (typeof ESIGN_DOCUMENT_PURPOSES)[number];

export type EsignDocumentType = {
  brokerLabel: string;
  signerTitle: string;
  /** Extra stored titles that should still use the signer-facing name. */
  stockTitles?: string[];
  summary: string;
  whyItMatters: string;
  /** Full sentences for the signer, with a `{sender}` placeholder. */
  emailIntro: string;
  whatItDoesNot?: string;
  estimatedMinutes?: number;
};

const DOCUMENT_TYPES: Partial<
  Record<EsignDocumentPurpose, EsignDocumentType>
> = {
  NDA: {
    brokerLabel: "NDA",
    signerTitle: "Non-Disclosure Agreement",
    summary: "Keeps the confidential details shared in this process private.",
    emailIntro:
      "This is a standard confidentiality agreement. It means the details shared in this process stay between you and {sender}.",
    whyItMatters:
      "It gives everyone a clear, shared expectation for handling sensitive information.",
    whatItDoesNot:
      "It does not commit you to selling, buying, or working with anyone.",
    estimatedMinutes: 3,
  },
  NDA_SELLER_BROKER: {
    brokerLabel: "Seller NDA",
    signerTitle: "Standard Seller Non-Disclosure Agreement",
    summary: "Keeps the details you share about your business private.",
    emailIntro:
      "This is a standard confidentiality agreement. It means anything you share about your business stays between you and {sender}.",
    whyItMatters:
      "It protects sensitive business information while you and your broker discuss next steps.",
    whatItDoesNot:
      "It does not commit you to selling or to working with anyone.",
    estimatedMinutes: 3,
  },
  NDA_BUYER_BROKER: {
    brokerLabel: "Buyer NDA",
    signerTitle: "Standard Buyer Non-Disclosure Agreement",
    summary: "Keeps confidential business information private while you evaluate an opportunity.",
    emailIntro:
      "This is a standard confidentiality agreement. It means the business details shared with you stay private between you and {sender}.",
    whyItMatters:
      "It sets a clear expectation for protecting information shared with you.",
    whatItDoesNot:
      "It does not commit you to buying a business.",
    estimatedMinutes: 3,
  },
  ACQUISITION_NDA: {
    brokerLabel: "Acquisition NDA",
    signerTitle: "Acquisition Non-Disclosure Agreement",
    stockTitles: ["Private target NDA"],
    summary: "Protects confidential information shared during an acquisition.",
    emailIntro:
      "This is a confidentiality agreement for an acquisition. It means the details you review stay between you and {sender}.",
    whyItMatters:
      "It lets the parties review sensitive details with a clear privacy agreement.",
    estimatedMinutes: 3,
  },
  LISTING_NDA: {
    brokerLabel: "Listing NDA",
    signerTitle: "Listing Non-Disclosure Agreement",
    summary: "Protects confidential details about a business listing.",
    emailIntro:
      "This is a confidentiality agreement for a business listing. It means the private details you review stay between you and {sender}.",
    whyItMatters:
      "It helps keep private business information private as you review the listing.",
    estimatedMinutes: 3,
  },
  LISTING_AGREEMENT: {
    brokerLabel: "Listing agreement",
    signerTitle: "Business Listing Agreement",
    summary: "Sets out how your business will be represented for sale.",
    emailIntro:
      "This agreement sets out how {sender} will represent your business for sale. It is a clear record of the services and what you can expect.",
    whyItMatters:
      "It gives you a clear record of the services and expectations for the listing.",
    estimatedMinutes: 8,
  },
  LOI: {
    brokerLabel: "Letter of intent",
    signerTitle: "Letter of Intent",
    summary: "Records the main terms of a proposed business transaction.",
    emailIntro:
      "This letter records the main terms of a proposed transaction with {sender}. It is a starting point for you to read before any final agreement.",
    whyItMatters:
      "It gives both parties a shared starting point before final agreements.",
    estimatedMinutes: 5,
  },
  STOCK_PURCHASE_AGREEMENT: {
    brokerLabel: "Stock purchase agreement",
    signerTitle: "Stock Purchase Agreement",
    summary: "Records the terms for a proposed purchase of company stock.",
    emailIntro:
      "This agreement records the terms for a proposed purchase of company stock. It is here so you and {sender} can read the same terms.",
    whyItMatters:
      "It puts the agreed transaction terms in one document for everyone to review.",
    estimatedMinutes: 10,
  },
  ASSET_PURCHASE_AGREEMENT: {
    brokerLabel: "Asset purchase agreement",
    signerTitle: "Asset Purchase Agreement",
    summary: "Records the terms for a proposed purchase of business assets.",
    emailIntro:
      "This agreement records the terms for a proposed purchase of business assets. It is here so you and {sender} can read the same terms.",
    whyItMatters:
      "It puts the agreed transaction terms in one document for everyone to review.",
    estimatedMinutes: 10,
  },
};

export function getEsignDocumentType(
  purpose: string | null | undefined,
): EsignDocumentType | null {
  if (!purpose || !(purpose in DOCUMENT_TYPES)) return null;
  return DOCUMENT_TYPES[purpose as EsignDocumentPurpose] ?? null;
}

export function getEsignDocumentLabel(
  purpose: string | null | undefined,
  fallback = "Document",
): string {
  return getEsignDocumentType(purpose)?.brokerLabel ?? fallback;
}

export function getEsignSignerTitle(
  purpose: string | null | undefined,
  fallback: string,
): string {
  return getEsignDocumentType(purpose)?.signerTitle ?? fallback;
}

function bareDocumentTitle(title: string): string {
  return title.trim().replace(/\.pdf$/i, "").trim();
}

function sameTitle(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** Short template and generated names, not a title the broker wrote. */
export function isStockEsignDocumentTitle(
  purpose: string | null | undefined,
  title: string,
): boolean {
  const bare = bareDocumentTitle(title);
  if (!bare) return true;
  const type = getEsignDocumentType(purpose);
  if (!type) return false;
  const labels = [type.brokerLabel, type.signerTitle, ...(type.stockTitles ?? [])];
  if (labels.some((label) => sameTitle(bare, label))) return true;
  return labels.some((label) => {
    const lower = bare.toLowerCase();
    const prefix = label.toLowerCase();
    return (
      lower.startsWith(`${prefix} — `) || lower.startsWith(`${prefix} - `)
    );
  });
}

/**
 * Name signers see in the email and on the signing page.
 * A broker's own title wins. Short template names use the signer-facing one.
 */
export function resolveSignerFacingTitle(
  purpose: string | null | undefined,
  title: string,
): string {
  const bare = bareDocumentTitle(title);
  const type = getEsignDocumentType(purpose);
  if (!type) return bare;
  if (isStockEsignDocumentTitle(purpose, bare)) return type.signerTitle;
  return bare;
}

/** Signer-facing email introduction, with the broker's first name filled in. */
export function getEsignEmailIntro(
  purpose: string | null | undefined,
  senderFirstName: string,
): string | null {
  const intro = getEsignDocumentType(purpose)?.emailIntro;
  if (!intro) return null;
  const name = senderFirstName.trim() || "your broker";
  return intro.replaceAll("{sender}", name);
}
